/** Smooth follow camera with directional look-ahead (PRD §4). */

import { CAMERA } from './config';
import { clamp, damp, mat4, multiply, lookAt, perspective, type Mat4 } from '../core/math';

/**
 * Limits on the player's view controls.
 *
 * ASYMMETRIC, and the asymmetry is the whole argument.
 *
 * Pulling BACK is what the narrow range was protecting against. This is a game
 * about not being able to see; the dark, the fog and the lamp cone are the
 * point of it, and a player who could pull back far enough would turn a
 * submarine into a map. 1.6 stays exactly where it was.
 *
 * Pushing IN gives no such advantage — it shows you LESS water, not more, and
 * no spawn, no AI and nothing in the simulation reads any of it (see
 * `userZoom`). So the near limit was never carrying the design; it was only
 * ever there for symmetry. 0.22 lets the boat fill the frame, which is what
 * you want when the hull has 12,689 triangles of fittings on it and you would
 * like to look at them.
 *
 * At 0.22 the framing is 8.4 m of water across a 38 m plane — a 7.2 m
 * submarine very nearly edge to edge.
 */
const ZOOM_MIN = 0.22;
const ZOOM_MAX = 1.6;
/** Screen fractions per pixel of scroll. */
const PAN_RATE = 0.0022;
/** Pan ceiling as a fraction of the half-extent — the boat stays on screen. */
const PAN_LIMIT = 0.55;
/** Seconds of no panning before the view eases back to the boat. */
const PAN_HOLD = 1.1;

export class Camera {
  x = 0;
  y = 0;
  /** Signed look-ahead offset, eased toward the direction of travel. */
  ahead = 0;
  aheadY = 0;
  zoom = 1;
  targetZoom = 1;

  /**
   * The player's own view controls, layered ON TOP of everything the game asks
   * for rather than replacing it.
   *
   * `targetZoom` is contested every frame — the nitro widens it, a firefight
   * widens it, Survival's front widens it, the boss framing sets it outright —
   * and any of those writers would stamp on a value the player had set. So the
   * player's zoom is a separate multiplier that survives all of them: pull back
   * to see more and the fight still opens the frame further from there.
   *
   * None of this reaches the simulation. It moves the eye and the culling
   * bounds that follow it, and nothing else — no spawn, no AI and no physics
   * reads any of it, so a player cannot zoom out to gain information the game
   * did not intend to give, only to see the same water more comfortably.
   */
  userZoom = 1;
  private userZoomTarget = 1;
  /** Player pan offset in metres, eased and sprung back to the boat. */
  panX = 0;
  panY = 0;
  private panTargetX = 0;
  private panTargetY = 0;
  /** Seconds since the player last panned, for the spring-back. */
  private panIdle = 99;

  shake = 0;
  private shakeX = 0;
  private shakeY = 0;
  private shakeSeed = Math.random() * 100;

  readonly view: Mat4 = mat4();
  readonly proj: Mat4 = mat4();
  readonly viewProj: Mat4 = mat4();
  /**
   * The same matrix without the temporal jitter, for motion vectors.
   *
   * Kept beside the jittered one rather than derived, because undoing a shift
   * that was applied inside a perspective divide is not a subtraction.
   */
  readonly viewProjNJ: Mat4 = mat4();
  /**
   * Sub-pixel offset applied to the projection, in NDC. Set by the renderer
   * from its sample sequence; zero when temporal antialiasing is off.
   *
   * This is the whole idea of TAA in one field: nudge the projection by less
   * than a pixel each frame so successive frames sample different points
   * inside the same pixel, then let the resolve add them up. What arrives is
   * more samples per pixel, paid for over time rather than all at once.
   */
  jitterX = 0;
  jitterY = 0;

  dist = 60;

  /**
   * How far the eye is orbited up and over the play plane, per view mode.
   *
   * A prototype, and deliberately shallow. The world is three slabs in a void —
   * the play layer is 19.2 m thick and the next one starts 36 m behind it,
   * meshed at 2.4x the cell size from a point 431.7 m away in x — so it only
   * holds together while the eye stays close to the axis it was authored for.
   * Twenty degrees is included to find where it breaks, not because it works.
   */
  static readonly TILT_DEGREES = [0, 10, 20] as const;

  /**
   * The z the camera is focused on — the plane the sub is flying in.
   *
   * Zero for the play lane, so nothing changes until the sub travels. Once it
   * does, the camera keeps its distance from the SUB rather than from the
   * origin, which is what turns the lane it left into a foreground it looks
   * past instead of a wall it is stuck behind.
   */
  focusZ = 0;

  /** Index into TILT_DEGREES. Cycled by V. */
  viewMode = 0;
  /**
   * The tilt actually being aimed for, radians. Was the table lookup itself.
   *
   * V still steps the three presets, and now writes the angle here rather than
   * being read for it — because the phone's view stick moves the tilt
   * CONTINUOUSLY and there is no index for 13 degrees. The presets are a
   * shortcut through a range, not the range.
   */
  private tiltTarget = 0;
  /** The top of that range: the largest preset, so both controls agree. */
  private static readonly TILT_MAX =
    (Math.max(...Camera.TILT_DEGREES) * Math.PI) / 180;
  /** Eased current angle in radians, so a mode change is a move, not a cut. */
  private tilt = 0;
  /** Last frame's dt, for easing inside `compute` where none is passed. */
  private lastDt = 1 / 60;
  aspect = 16 / 9;

  snapTo(x: number, y: number) {
    this.x = x + this.bias;
    this.y = y;
    this.ahead = 0;
    this.aheadY = 0;
  }

  /**
   * Shake, with diminishing returns.
   *
   * This used to add linearly and clamp, which is fine when shake events are
   * occasional. Survival's front fires dozens of small ones a second — every
   * hit, every kill, every torpedo — and they pinned the camera at the ceiling
   * and held it there for the whole wave. Reported, accurately, as "shaking
   * like crazy, not a nice feeling".
   *
   * Scaling each contribution by the headroom left means a boss dying still
   * lands its full 1.0 from calm, while the sixtieth 0.05 of the second barely
   * moves. Same ceiling, same big moments, no permanent tremor.
   */
  addShake(amount: number) {
    const room = 1 - this.shake / 1.6;
    this.shake = Math.min(1.6, this.shake + amount * room);
  }

  /**
   * Terminal speeds of whatever is being followed. Set when the hull changes;
   * these used to be the Pathfinder's numbers hard-coded, which would have made
   * a slower boat look permanently becalmed and a faster one always flat out.
   */
  maxSpeedX = 16;
  maxSpeedY = 12;

  /**
   * Standing offset of the camera from whatever it follows, in metres.
   *
   * The campaign leaves this at 0 and centres the boat, which is right when the
   * threat can be in any direction. A tide has ONE direction, so centring wastes
   * half the screen on water nothing will ever come out of: the arena the player
   * is retreating across. Pushing the camera toward the threat puts the boat
   * left of centre and roughly doubles how far ahead the front is visible, which
   * is the difference between choosing when to meet a wave and being met by it.
   *
   * Applied to the follow target, not to the rendered position, so the existing
   * look-ahead still layers on top and the whole thing still eases.
   */
  bias = 0;

  /**
   * Multiplier on the horizontal look-ahead, set per frame by the world.
   *
   * Drives the "sub drifts to the side it is travelling towards" behaviour:
   * lead scales with velocity already, and this lets the nitro push it further
   * still, so a boat at full burn sits well back in the frame with the water it
   * is about to cross laid out ahead of it.
   */
  leadScale = 1;

  /**
   * Re-scale the look-ahead for a different hull.
   *
   * Deliberately proportional rather than set to the terminal speed outright:
   * the 16 and 12 were hand-tuned against the Pathfinder and are close to but
   * not equal to its terminals (15.12 and 9.2), so adopting the terminal
   * directly would quietly re-tune the camera on the boat everybody already
   * knows. This keeps the Pathfinder exactly as it was and gives a slower boat
   * a proportionally shorter lead.
   */
  followChassis(terminalX: number, terminalY: number, refX: number, refY: number) {
    this.maxSpeedX = 16 * (terminalX / refX);
    this.maxSpeedY = 12 * (terminalY / refY);
  }

  /**
   * Feed the player's view controls. Called before `update`, from the one place
   * that has the input — and only during play, so a menu never eats a scroll.
   *
   * Pan comes in as screen fractions and is stored in metres, so the same
   * gesture moves the view the same distance across the screen at any zoom.
   */
  viewControls(
    zoomDelta: number, panX: number, panY: number, reset: boolean, tilt = 0,
  ) {
    if (reset) {
      this.userZoomTarget = 1;
      this.panTargetX = 0;
      this.panTargetY = 0;
      this.panIdle = 99;
      // Back to whichever preset V last chose, not to flat: reset means "undo
      // what I just dragged", and the keyboard's choice was not dragged.
      this.tiltTarget = ((Camera.TILT_DEGREES[this.viewMode] ?? 0) * Math.PI) / 180;
      return;
    }
    if (tilt !== 0) {
      this.tiltTarget = clamp(this.tiltTarget + tilt, 0, Camera.TILT_MAX);
    }
    if (zoomDelta !== 0) {
      // Multiplicative, so a step feels the same size wherever you are in the
      // range — additive steps crawl when zoomed out and lurch when zoomed in.
      this.userZoomTarget = clamp(this.userZoomTarget * Math.exp(zoomDelta), ZOOM_MIN, ZOOM_MAX);
    }
    if (panX !== 0 || panY !== 0) {
      const { w, h } = this.halfExtent();
      // Clamped to a fraction of what is visible rather than to a fixed number
      // of metres, so the boat can never be pushed off the edge of the screen
      // at any zoom. Panning is for looking around, not for scouting ahead.
      this.panTargetX = clamp(this.panTargetX + panX * PAN_RATE * w, -w * PAN_LIMIT, w * PAN_LIMIT);
      this.panTargetY = clamp(this.panTargetY - panY * PAN_RATE * h, -h * PAN_LIMIT, h * PAN_LIMIT);
      this.panIdle = 0;
    }
  }

  update(targetX: number, targetY: number, velX: number, velY: number, dt: number, time: number) {
    this.lastDt = dt;
    const wantAhead = clamp(velX / this.maxSpeedX, -1, 1) * CAMERA.lookAheadX * this.leadScale;
    const wantAheadY = clamp(velY / this.maxSpeedY, -1, 1) * CAMERA.lookAheadY;
    this.ahead = damp(this.ahead, wantAhead, CAMERA.lookAheadRate, dt);
    this.aheadY = damp(this.aheadY, wantAheadY, CAMERA.lookAheadRate * 0.8, dt);

    this.x = damp(this.x, targetX + this.ahead + this.bias, CAMERA.followX, dt);
    this.y = damp(this.y, targetY + this.aheadY, CAMERA.followY, dt);

    this.zoom = damp(this.zoom, this.targetZoom, 2.4, dt);

    // The pan springs back to the boat once the player stops asking for it.
    // A view that stays where it was put is a view the player has to remember
    // to put back, and the one moment they will not remember is the moment
    // something starts shooting.
    this.panIdle += dt;
    if (this.panIdle > PAN_HOLD) {
      this.panTargetX = damp(this.panTargetX, 0, 2.2, dt);
      this.panTargetY = damp(this.panTargetY, 0, 2.2, dt);
    }
    this.panX = damp(this.panX, this.panTargetX, 9, dt);
    this.panY = damp(this.panY, this.panTargetY, 9, dt);
    this.userZoom = damp(this.userZoom, this.userZoomTarget, 9, dt);

    this.shake = Math.max(0, this.shake - CAMERA.shakeDecay * this.shake * dt - 0.02 * dt);

    const s = this.shake * this.shake;
    const t = time * 34 + this.shakeSeed;
    this.shakeX = (Math.sin(t * 1.7) + Math.sin(t * 3.31) * 0.5) * s * 1.5;
    this.shakeY = (Math.cos(t * 2.13) + Math.cos(t * 4.07) * 0.5) * s * 1.2;
  }

  resize(aspect: number) {
    this.aspect = aspect;
  }

  /**
   * The zoom actually rendered: what the game asked for, times what the player
   * asked for. Everything that needs to agree with the picture — the culling
   * bounds, the fog extent, the HUD's sense of scale — must read THIS and not
   * `zoom`, or it will quietly disagree with the frame by the player's factor.
   */
  viewZoom(): number {
    return this.zoom * this.userZoom;
  }

  /** Centre of the rendered view, pan included. Not the follow target. */
  viewX(): number { return this.x + this.panX; }
  viewY(): number { return this.y + this.panY; }

  compute(): Mat4 {
    const viewHeight = CAMERA.viewHeight * this.viewZoom();
    this.dist = viewHeight / 2 / Math.tan(CAMERA.fov / 2);
    const ex = this.x + this.panX + this.shakeX;
    const ey = this.y + this.panY + this.shakeY;
    // Orbit the eye up and over, still aimed at the same point on the play
    // plane, so the tilt costs nothing but a different `lookAt`. The angle is
    // eased rather than snapped: a jump straight to twenty degrees reads as a
    // glitch, and TAA reprojects a hard cut as a smear.
    this.tilt = damp(this.tilt, this.tiltTarget, 6, this.lastDt);
    const s = Math.sin(this.tilt), c = Math.cos(this.tilt);
    lookAt(
      this.view,
      { x: ex, y: ey + s * this.dist, z: this.focusZ + c * this.dist },
      { x: ex, y: ey, z: this.focusZ },
    );
    perspective(this.proj, CAMERA.fov, this.aspect, 0.8, 2600);
    multiply(this.viewProjNJ, this.proj, this.view);
    // Applied to the projection's third column: with w = -z, adding here is a
    // constant shift in NDC once the divide has happened, which is exactly a
    // sub-pixel nudge of the whole image and not a skew.
    this.proj[8] = -this.jitterX;
    this.proj[9] = -this.jitterY;
    return multiply(this.viewProj, this.proj, this.view);
  }

  /** Half-extents of the visible area on the gameplay plane. */
  halfExtent(): { w: number; h: number } {
    const h = (CAMERA.viewHeight * this.viewZoom()) / 2;
    return { w: h * this.aspect, h };
  }

  /**
   * Where the eye actually is. This has to follow the orbit, not the old
   * head-on assumption: `world.ts` culls the foreground weed by projecting it
   * from here, so an eye that lies about its position culls the wrong plants.
   */
  eye(): { x: number; y: number; z: number } {
    return {
      x: this.x + this.panX + this.shakeX,
      y: this.y + this.panY + this.shakeY + Math.sin(this.tilt) * this.dist,
      z: this.focusZ + Math.cos(this.tilt) * this.dist,
    };
  }

  /** Current eased tilt in radians. Anything culling against the frustum needs it. */
  viewTilt(): number { return this.tilt; }

  /** Next view mode, wrapping. Returns the one now selected. */
  cycleView(): number {
    this.viewMode = (this.viewMode + 1) % Camera.TILT_DEGREES.length;
    this.tiltTarget = ((Camera.TILT_DEGREES[this.viewMode] ?? 0) * Math.PI) / 180;
    return this.viewMode;
  }
}
